import { useRef, type CSSProperties } from "react";
import { carre } from "../guide/guideStyles.js";
import { useEtatInfos } from "./etatInfos.js";
import { IconeInfo, IconePlus } from "./icones.js";
import { boutonInfo, boutonPlus, filetVertical, groupeVerre } from "./infosStyles.js";
import { useInfos } from "./useInfos.js";
import "./infos.css";

/**
 * ============================================================================
 *  BoutonInfos.tsx — Le bouton qui ouvre le menu d'informations
 * ----------------------------------------------------------------------------
 *  Un seul composant, posé à quatre endroits (le menu, la feuille et les crédits
 *  sont montés une fois, par `InfoMenu`) :
 *
 *   - `groupe` : ⓘ de 44 px dans le groupe en verre dépoli des langues (ordinateur) ;
 *   - `barre`  : ⋯ de 52 px à droite de la barre de navigation du bas (téléphone),
 *                séparé d'elle par un filet ;
 *   - `bande`  : dans les bandes noires du guide (contour, comme les autres commandes) ;
 *   - `verre`  : en mode main, par-dessus l'image, à côté de « Quitter ».
 *
 *  `variante` ne change que le glyphe et le rôle annoncé : ⓘ ouvre un menu, ⋯ une feuille.
 * ==========================================================================*/

export type HabillageBouton = "groupe" | "barre" | "bande" | "verre";

export function BoutonInfos({
  variante,
  habillage,
  style,
}: {
  variante: "info" | "plus";
  habillage: HabillageBouton;
  style?: CSSProperties;
}) {
  const { t, basculerMenu } = useInfos();
  const ouvert = useEtatInfos((e) => e.session.menu);
  const bouton = useRef<HTMLButtonElement>(null);

  const base: CSSProperties =
    habillage === "barre"
      ? boutonPlus
      : habillage === "bande"
        ? carre(44, ouvert)
        : habillage === "verre"
          ? { ...groupeVerre, ...boutonInfo(ouvert), border: "1px solid rgba(246,238,227,.4)" }
          : boutonInfo(ouvert);

  return (
    <button
      ref={bouton}
      type="button"
      className="inf-bouton"
      aria-label={t("infoOuvrir")}
      aria-haspopup={variante === "info" ? "menu" : "dialog"}
      aria-expanded={ouvert}
      onClick={() => basculerMenu(bouton.current)}
      style={{ ...base, ...style }}
    >
      {habillage === "barre" && <span aria-hidden style={filetVertical} />}
      {variante === "info" ? <IconeInfo taille={22} /> : <IconePlus taille={habillage === "barre" ? 24 : 22} />}
    </button>
  );
}
